import React, { useEffect, useRef, useState } from "react";
import "./SchoolV3.css";

import engineeringStudents from "../assets/img/indian-engineering-students.webp";
import learningExperience from "../assets/img/indian-learning-experience.webp";

const programmes = [
  {
    code: "CSE",
    title: "Computer Science & Engineering",
    degree: "B.Tech",
    duration: "4 Years",
    level: "Undergraduate",
    description: "Software engineering, artificial intelligence, data science, cloud computing and cybersecurity.",
  },
  {
    code: "ME",
    title: "Mechanical Engineering",
    degree: "B.Tech",
    duration: "4 Years",
    level: "Undergraduate",
    description: "Design, manufacturing, thermal systems, automation, materials and sustainable engineering.",
  },
  {
    code: "CE",
    title: "Civil Engineering",
    degree: "B.Tech",
    duration: "4 Years",
    level: "Undergraduate",
    description: "Structures, construction, transportation, water systems and sustainable urban development.",
  },
  {
    code: "EEE",
    title: "Electrical & Electronics",
    degree: "B.Tech",
    duration: "4 Years",
    level: "Undergraduate",
    description: "Power systems, electrical machines, control, renewable energy and embedded technologies.",
  },
  {
    code: "MTC",
    title: "Engineering Specialisations",
    degree: "M.Tech",
    duration: "2 Years",
    level: "Postgraduate",
    description: "Advanced disciplinary study supported by research, specialist laboratories and dissertation work.",
  },
  {
    code: "DIP",
    title: "Diploma in Engineering",
    degree: "Diploma",
    duration: "3 Years",
    level: "Diploma",
    description: "Practice-oriented technical education supporting industry readiness and further academic progression.",
  },
];

const facilities = [
  {
    number: "01",
    title: "Computing & AI Lab",
    text: "High-performance systems for programming, data science and intelligent application development.",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=88",
  },
  {
    number: "02",
    title: "Mechanical Workshop",
    text: "Hands-on spaces supporting manufacturing, fabrication, measurement and prototype development.",
    image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&w=1200&q=88",
  },
  {
    number: "03",
    title: "Civil Engineering Lab",
    text: "Specialist facilities for material testing, surveying, structures and environmental engineering.",
    image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=88",
  },
  {
    number: "04",
    title: "Innovation & Incubation",
    text: "A collaborative platform where student ideas develop into prototypes, ventures and social solutions.",
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=88",
  },
];

const faqs = [
  {
    question: "What programmes are offered by the School of Engineering & Technology?",
    answer:
      "The School offers undergraduate, postgraduate, diploma and doctoral opportunities across major engineering disciplines. Replace this sample list with the University’s officially approved current programmes.",
  },
  {
    question: "What is the general eligibility for B.Tech admission?",
    answer:
      "Applicants generally require 10+2 with Physics and Mathematics along with another approved subject. Candidates should verify the latest eligibility and admission requirements before applying.",
  },
  {
    question: "Are students provided practical and industry exposure?",
    answer:
      "Yes. The learning model includes laboratories, workshops, industrial visits, expert sessions, internships, mini-projects and capstone work, subject to programme requirements.",
  },
  {
    question: "Does the University provide placement assistance?",
    answer:
      "Students receive structured career support through aptitude development, technical preparation, communication training, résumé guidance, mock interviews and recruitment opportunities.",
  },
  {
    question: "How can students apply for admission?",
    answer:
      "Candidates can complete the online application form and connect with the admissions team for programme selection, eligibility verification and further guidance.",
  },
];

function AnimatedNumber({ value, suffix = "" }) {
  const [display, setDisplay] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        const start = performance.now();
        const duration = 1450;

        function frame(now) {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(Math.round(value * eased));
          if (progress < 1) requestAnimationFrame(frame);
        }

        requestAnimationFrame(frame);
        observer.disconnect();
      },
      { threshold: 0.35 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [value]);

  return <strong ref={elementRef}>{display}{suffix}</strong>;
}

function Heading({ eyebrow, title, accent, description, center = false, light = false }) {
  return (
    <header className={`s3-heading ${center ? "s3-heading--center" : ""} ${light ? "s3-heading--light" : ""}`}>
      <span>{eyebrow}</span>
      <h2>{title} <em>{accent}</em></h2>
      {description && <p>{description}</p>}
    </header>
  );
}

export default function SchoolV3() {
  const [programmeLevel, setProgrammeLevel] = useState("All");
  const [openFaq, setOpenFaq] = useState(0);

  const levels = ["All", "Undergraduate", "Postgraduate", "Diploma"];
  const visibleProgrammes =
    programmeLevel === "All"
      ? programmes
      : programmes.filter((programme) => programme.level === programmeLevel);

  useEffect(() => {
    const elements = document.querySelectorAll(".school-v3 .s3-reveal:not(.s3-show)");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("s3-show");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.09, rootMargin: "0px 0px -35px" }
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [programmeLevel]);

  return (
    <main className="school-v3">
      <section className="s3-hero" id="overview">
        <div className="s3-hero__topline">
          <div className="s3-shell">
            <span>School of Engineering & Technology</span>
            <span>ISBM University · Chhattisgarh</span>
          </div>
        </div>

        <div className="s3-hero__pattern" aria-hidden="true">
          <i /><i /><i /><i />
        </div>

        <div className="s3-shell s3-hero__grid">
          <div className="s3-hero__copy">
            <nav aria-label="Breadcrumb">
              <a href="/">Home</a><span>/</span><a href="/academics">Academic Schools</a><span>/</span><b>Engineering</b>
            </nav>
            <span className="s3-kicker">Technology · Innovation · Nation Building</span>
            <h1>
              Engineering
              <small>ideas for a</small>
              <em>better India.</em>
            </h1>
            <p>
              A future-focused engineering school where Indian values, scientific
              thinking and practical innovation come together.
            </p>
            <div className="s3-hero__actions">
              <a href="/apply-online" className="s3-button s3-button--burgundy">Apply for admission <span>↗</span></a>
              <a href="#programmes" className="s3-button s3-button--outline">Explore programmes <span>↓</span></a>
            </div>
          </div>

          <div className="s3-hero__media">
            <div className="s3-hero__arch">
              <img src={engineeringStudents} alt="Indian engineering students working on a robotics project" />
            </div>
            <div className="s3-hero__stamp">
              <span>Future-ready</span>
              <b>ENGINEERING</b>
              <span>Practical learning</span>
            </div>
            <article className="s3-hero__feature">
              <span>Featured experience</span>
              <b>Learn through ideas, experiments and real projects.</b>
              <a href="#experience">Discover the experience ↗</a>
            </article>
          </div>
        </div>
      </section>

      <section className="s3-numbers">
        <div className="s3-shell">
          <article><AnimatedNumber value={12} suffix="+" /><span>Academic programmes</span></article>
          <article><AnimatedNumber value={30} suffix="+" /><span>Labs and practical spaces</span></article>
          <article><AnimatedNumber value={250} suffix="+" /><span>Recruiting organisations*</span></article>
          <article><AnimatedNumber value={95} suffix="%" /><span>Placement assistance*</span></article>
        </div>
      </section>

      <nav className="s3-navigation" aria-label="School details navigation">
        <div className="s3-shell">
          {[
            ["About", "about"],
            ["Programmes", "programmes"],
            ["Experience", "experience"],
            ["Facilities", "facilities"],
            ["Research", "research"],
            ["Faculty", "faculty"],
            ["Careers", "careers"],
            ["FAQ", "faq"],
          ].map(([label, id]) => <a href={`#${id}`} key={id}>{label}</a>)}
        </div>
      </nav>

      <section className="s3-manifesto s3-section" id="about">
        <div className="s3-shell">
          <div className="s3-manifesto__title s3-reveal">
            <span>Our academic philosophy</span>
            <h2>Knowledge that is <em>rooted.</em><br />Thinking that is <em>limitless.</em></h2>
          </div>
          <div className="s3-manifesto__content s3-reveal">
            <p className="s3-manifesto__lead">
              The School develops technically capable, ethically grounded and
              socially conscious engineers equipped to contribute to India’s
              evolving industries and communities.
            </p>
            <p>
              Students experience an environment that values academic rigour,
              interdisciplinary exploration and learning by doing. Contemporary
              engineering education is strengthened by Indian Knowledge Systems,
              sustainability, entrepreneurship and an understanding of local needs.
            </p>
            <a className="s3-text-link" href="/about-school">Read about the School <span>↗</span></a>
          </div>
          <div className="s3-manifesto__pillars">
            <article className="s3-reveal"><span>01</span><h3>Rigour</h3><p>Strong fundamentals and disciplined technical thinking.</p></article>
            <article className="s3-reveal"><span>02</span><h3>Relevance</h3><p>Curriculum connected to industry, society and emerging technology.</p></article>
            <article className="s3-reveal"><span>03</span><h3>Responsibility</h3><p>Engineering decisions shaped by ethics and sustainability.</p></article>
            <article className="s3-reveal"><span>04</span><h3>Resourcefulness</h3><p>Confidence to create practical solutions with measurable impact.</p></article>
          </div>
        </div>
      </section>

      <section className="s3-programmes s3-section" id="programmes">
        <div className="s3-shell">
          <div className="s3-programmes__heading s3-reveal">
            <Heading
              eyebrow="Programme finder"
              title="Choose your"
              accent="engineering pathway."
              description="Explore career-focused programmes that balance academic depth, practical learning and professional preparation."
              light
            />
            <a href="/programmes" className="s3-circle-link" aria-label="View every programme">↗</a>
          </div>

          <div className="s3-programmes__filters" role="tablist">
            {levels.map((level) => (
              <button
                type="button"
                role="tab"
                aria-selected={programmeLevel === level}
                className={programmeLevel === level ? "active" : ""}
                onClick={() => setProgrammeLevel(level)}
                key={level}
              >
                {level}
              </button>
            ))}
          </div>

          <div className="s3-programmes__grid">
            {visibleProgrammes.map((programme, index) => (
              <article className="s3-programme s3-reveal s3-show" key={`${programme.degree}-${programme.code}`}>
                <div className="s3-programme__top">
                  <span>{programme.code}</span><small>{String(index + 1).padStart(2, "0")}</small>
                </div>
                <b>{programme.degree}</b>
                <h3>{programme.title}</h3>
                <p>{programme.description}</p>
                <footer>
                  <span>{programme.duration}</span>
                  <a href="/programme-details" aria-label={`Explore ${programme.title}`}>↗</a>
                </footer>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="s3-experience s3-section" id="experience">
        <div className="s3-shell">
          <Heading
            eyebrow="The learning experience"
            title="Four dimensions of"
            accent="future-ready learning."
            description="Every stage of the student journey is designed to move knowledge into confident action."
            center
          />

          <div className="s3-experience__canvas">
            <div className="s3-experience__image s3-reveal">
              <img src={learningExperience} alt="Indian students and faculty engaged in practical learning" loading="lazy" />
              <span>Learn together.<br />Build together.</span>
            </div>
            <div className="s3-experience__cards">
              <article className="s3-reveal"><span>01</span><h3>Conceptual clarity</h3><p>Interactive teaching develops strong foundations and analytical confidence.</p></article>
              <article className="s3-reveal"><span>02</span><h3>Hands-on practice</h3><p>Laboratories and workshops allow students to test, build and improve.</p></article>
              <article className="s3-reveal"><span>03</span><h3>Industry exposure</h3><p>Expert sessions, industrial visits, internships and live assignments build relevance.</p></article>
              <article className="s3-reveal"><span>04</span><h3>Human capabilities</h3><p>Communication, teamwork, leadership and ethics strengthen professional readiness.</p></article>
            </div>
          </div>
        </div>
      </section>

      <section className="s3-facilities s3-section" id="facilities">
        <div className="s3-shell">
          <div className="s3-facilities__top s3-reveal">
            <Heading
              eyebrow="Infrastructure"
              title="Purpose-built spaces."
              accent="Possibility without limits."
              description="Facilities that encourage experimentation, collaboration and the transformation of ideas into working outcomes."
            />
            <a className="s3-text-link" href="/facilities">View all facilities <span>↗</span></a>
          </div>
        </div>

        <div className="s3-facilities__rail">
          {facilities.map((facility) => (
            <article className="s3-facility s3-reveal" key={facility.title}>
              <div><img src={facility.image} alt={facility.title} loading="lazy" /><span>{facility.number}</span></div>
              <h3>{facility.title}</h3><p>{facility.text}</p>
              <a href="/facilities">Explore facility ↗</a>
            </article>
          ))}
        </div>
      </section>

      <section className="s3-research s3-section" id="research">
        <div className="s3-shell s3-research__grid">
          <div className="s3-research__copy s3-reveal">
            <Heading
              eyebrow="Research & innovation"
              title="Research inspired by"
              accent="India’s possibilities."
              description="Students and faculty investigate challenges that matter—from intelligent technologies and smart infrastructure to clean energy and advanced manufacturing."
            />
            <div className="s3-research__topics">
              {["Artificial Intelligence & Data", "Smart Cities & Infrastructure", "Renewable Energy Systems", "Advanced Manufacturing"].map((topic, index) => (
                <a href="/research" key={topic}><span>0{index + 1}</span><b>{topic}</b><i>↗</i></a>
              ))}
            </div>
          </div>

          <div className="s3-research__visual s3-reveal">
            <img
              src="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&w=1200&q=88"
              alt="Engineering research laboratory"
              loading="lazy"
            />
            <div className="s3-research__metrics">
              <article><AnimatedNumber value={120} suffix="+" /><span>Publications*</span></article>
              <article><AnimatedNumber value={18} suffix="+" /><span>Patents*</span></article>
              <article><AnimatedNumber value={35} suffix="+" /><span>Projects*</span></article>
            </div>
          </div>
        </div>
      </section>

      <section className="s3-dean s3-section" id="faculty">
        <div className="s3-shell s3-dean__grid">
          <div className="s3-dean__portrait s3-reveal">
            <img
              src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1000&q=88"
              alt="Dean of the School of Engineering and Technology"
              loading="lazy"
            />
            <div><span>Academic leadership</span><b>Dean’s message</b></div>
          </div>
          <blockquote className="s3-reveal">
            <span>“</span>
            <p>
              Our aim is to nurture engineers who combine knowledge with
              integrity, innovation with empathy and ambition with a genuine
              commitment to India’s progress.
            </p>
            <footer><b>Prof. (Dr.) Your Dean Name</b><small>Dean · School of Engineering & Technology</small></footer>
            <a className="s3-text-link" href="/faculty">Meet our faculty <span>↗</span></a>
          </blockquote>
        </div>
      </section>

      <section className="s3-careers" id="careers">
        <div className="s3-shell">
          <div className="s3-careers__heading s3-reveal">
            <Heading
              eyebrow="Career readiness"
              title="From campus potential"
              accent="to professional impact."
              description="A progressive career journey strengthens every student’s technical confidence, professional communication and ability to engage with opportunity."
              light
            />
            <a href="/placements" className="s3-button s3-button--gold">Explore placements <span>↗</span></a>
          </div>

          <div className="s3-careers__path">
            <article className="s3-reveal"><span>Year 01</span><b>Discover strengths</b><p>Orientation, communication foundations and career awareness.</p></article>
            <article className="s3-reveal"><span>Year 02</span><b>Build capability</b><p>Aptitude, technical practice and collaborative projects.</p></article>
            <article className="s3-reveal"><span>Year 03</span><b>Gain exposure</b><p>Internships, live briefs, mentoring and industry interaction.</p></article>
            <article className="s3-reveal"><span>Year 04</span><b>Launch confidently</b><p>Interview preparation, recruitment drives and career transition.</p></article>
          </div>

          <p className="s3-note">*All numerical statistics shown on this demonstration page should be replaced with verified current University data.</p>
        </div>
      </section>

      <section className="s3-faq s3-section" id="faq">
        <div className="s3-shell s3-faq__grid">
          <div className="s3-faq__intro s3-reveal">
            <Heading
              eyebrow="Questions & answers"
              title="Planning your"
              accent="next step?"
              description="Find answers to common questions about programmes, learning and career support."
            />
            <div className="s3-faq__help">
              <span>Admissions support</span>
              <b>+91-9373199999</b>
              <a href="/contact">Contact the University ↗</a>
            </div>
          </div>

          <div className="s3-faq__list s3-reveal">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <article className={isOpen ? "open" : ""} key={faq.question}>
                  <button type="button" aria-expanded={isOpen} onClick={() => setOpenFaq(isOpen ? -1 : index)}>
                    <span>{String(index + 1).padStart(2, "0")}</span><b>{faq.question}</b><i>{isOpen ? "−" : "+"}</i>
                  </button>
                  <div><p>{faq.answer}</p></div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="s3-admission">
        <div className="s3-admission__arches" aria-hidden="true"><i /><i /><i /></div>
        <div className="s3-shell s3-admission__content s3-reveal">
          <span>Admissions open</span>
          <h2>Engineer your <em>next chapter.</em></h2>
          <p>Take the first step towards a future shaped by knowledge, purpose and possibility.</p>
          <div>
            <a href="/apply-online" className="s3-button s3-button--burgundy">Apply online <span>↗</span></a>
            <a href="/download-brochure" className="s3-button s3-button--outline-light">Download brochure <span>↓</span></a>
          </div>
        </div>
      </section>
    </main>
  );
}
